import { useCallback, type MouseEvent, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface QuestionnaireAttachmentDeleteDialogProps {
  open: boolean;
  filename?: string;
  /** Własny tytuł (domyślnie pytanie o załącznik ankiety) */
  title?: string;
  /** Skutek usunięcia pod nazwą pliku (np. zmiana potwierdzenia rekrutera przy ostatnim skanie) */
  description?: string;
  /**
   * Stan „trwa usuwanie" (np. `isDeleting` z mutacji). Gdy podany, okno nie zamyka się po „Usuń" —
   * zamyka je wywołujący (`open=false` po odpowiedzi serwera); w trakcie przyciski są nieaktywne,
   * a Esc nic nie robi. Bez tej właściwości zachowanie dotychczasowe (ankieta): zamknięcie od razu.
   */
  isBusy?: boolean;
  /** Element, który ma dostać fokus po zamknięciu (wyzwalacz „Usuń" znika razem z wierszem). */
  returnFocusRef?: RefObject<HTMLElement | null>;
  onConfirm: () => void;
  onClose: () => void;
}

export function QuestionnaireAttachmentDeleteDialog({
  open,
  filename,
  title,
  description,
  isBusy,
  returnFocusRef,
  onConfirm,
  onClose,
}: QuestionnaireAttachmentDeleteDialogProps) {
  const { t } = useTranslation();
  const managedByCaller = isBusy !== undefined;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen || isBusy) return;
      onClose();
    },
    [isBusy, onClose],
  );

  const handleConfirm = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // Radix zamyka okno po kliknięciu akcji; przy stanie zarządzanym przez wywołującego
      // okno zostaje otwarte do odpowiedzi serwera.
      if (managedByCaller) event.preventDefault();
      onConfirm();
    },
    [managedByCaller, onConfirm],
  );

  const handleCancel = useCallback(() => {
    if (isBusy) return;
    onClose();
  }, [isBusy, onClose]);

  const handleCloseAutoFocus = useCallback(
    (event: Event) => {
      const target = returnFocusRef?.current;
      if (!target) return;
      event.preventDefault();
      target.focus();
    },
    [returnFocusRef],
  );

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent onCloseAutoFocus={handleCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {title ?? t("fileAttachments.confirmDeleteTitle")}
          </AlertDialogTitle>
          {(filename || description) && (
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {filename && (
                  <p
                    className="break-words break-all min-w-0 max-w-full font-medium text-foreground"
                    title={filename}
                    style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                  >
                    {filename}
                  </p>
                )}
                {description && <p>{description}</p>}
              </div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={isBusy}>
            {t("common.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isBusy}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isBusy ? (
              <>
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
                {t("common.deleting")}
              </>
            ) : (
              t("common.delete")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
